import { useEffect } from 'react'
import { useIntl } from 'react-intl'

import styled from '@emotion/styled'
import {
  CAMPAIGN_CHANNEL_EMAIL,
  CAMPAIGN_CHANNEL_MESSENGER,
  CAMPAIGN_CHANNEL_TELEGRAM
} from 'Constants/'
import {
  fromUTCtoZoneFormatted,
  notifyParent,
  STORE_KEY_LIST,
  storeGet
} from 'mailup-react-helpers'
import {
  DataListExtraColumn,
  DataListHeader,
  DataListRow,
  Grid,
  GridCell,
  Icon,
  ICON_FA_VARIANT_SOLID,
  IconFAExclamationTriangle,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  Panel,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  Text,
  TEXT_VARIANT_LIGHT,
  Toggle,
  TooltipUtils,
  TruncateAndTooltip
} from 'mailup-web-components'
import iconTelegram from 'mailup-web-components/resources/mailup-web-app/img/svg/campaign-list-channel_telegram.svg'
import { useHistory } from 'react-router-dom'
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil'
import {
  getCampaignListCallback,
  updateStatusCallback
} from '../recoil/callbacks/callbacks'
import { pageState, resultsState, searchState } from '../recoil/state/page'
import ContentSource from './ContentSource'
import Pagination from './Pagination'
import TableActionMenu from './TableActionMenu'

const StyledIcon = styled(Icon)`
  font-size: 18px;
`
const StyledDataListExtraColumn = styled(DataListExtraColumn)`
  min-width: 33px;
`

export const StyledIconWarningTriangle = styled(IconFAExclamationTriangle)`
  color: #fa4747;
  font-size: 13px;
  position: relative;
  bottom: -7px;
`

const Table = () => {
  const intl = useIntl()
  const { orderBy, orderDescending, pageSize } = useRecoilValue(searchState)
  const { items, loadingTable } = useRecoilValue(resultsState)
  const history = useHistory()
  const [{ updateStatusInProgress, updateStatusIdCampaign }, setPageState] =
    useRecoilState(pageState)

  const getCampaignList = useRecoilCallback(getCampaignListCallback, [])
  const updateStatus = useRecoilCallback(updateStatusCallback, [])

  const { TimeZoneCode } = storeGet(STORE_KEY_LIST)
  const EXTRA_COLUMN_AFTER_WIDTH = '30px'
  const EXTRA_COLUMN_BEFORE_WIDTH = '33px'

  useEffect(() => {
    setTimeout(() => TooltipUtils.rebuild())
  }, [loadingTable])

  const handleClickSort = (filed) => {
    let orderD = orderDescending
    if (filed === orderBy) {
      orderD = !orderDescending
    }

    getCampaignList({ orderBy: filed, orderDescending: orderD })
  }

  const handleEdit = (item) => {
    setPageState((prev) => ({
      ...prev,
      loading: true
    }))
    if (item.Type === 1) {
      history.push('/automation/contentsCampaign/' + item.Id + '/0')
    } else {
      notifyParent('redirectMainFrame', [
        'App.aspx#/contentsCampaignWizard/' + item.Id + '/0/' + item.Type
      ])
    }
  }

  const handelChangeStatus = (item) => {
    updateStatus(item.Id, item.Enabled, item.Name, item.Type)
  }

  const heads = [
    {
      field: 'Name',
      label: intl.formatMessage({
        id: 'Load.ContentsCampaignList.Campaign'
      }),
      span: 2,
      sortable: true
    },
    {
      span: 5,
      label: intl.formatMessage({
        id: 'Load.ContentsCampaignList.Source'
      }),
      sortable: false
    },
    {
      span: 2,
      label: intl.formatMessage({
        id: 'Load.MailupCommon.LastUpdate'
      }),
      sortable: true,
      field: 'UpdateDate'
    },
    {
      span: 2,
      label: intl.formatMessage({
        id: 'Load.MailupCommon.LastSend'
      }),
      sortable: false
    },
    {
      span: 1,
      label: intl.formatMessage({
        id: 'Load.MailupCommon.Status'
      }),
      sortable: false
    }
  ]

  return (
    <Panel>
      <DataListHeader
        data-mu-test='contentsCampaign--header'
        heads={heads}
        beforeWidth={EXTRA_COLUMN_BEFORE_WIDTH}
        afterWidth={EXTRA_COLUMN_AFTER_WIDTH}
        orderByStatus={orderBy}
        orderDescendingStatus={orderDescending}
        handleChangeColumnsOrderCallback={handleClickSort}
      />
      <div>
        {!loadingTable && (
          <>
            {items.map((item, key) => (
              <DataListRow key={key} data-mu-test='contentsCampaign--list-item'>
                <StyledDataListExtraColumn
                  before
                  width={EXTRA_COLUMN_BEFORE_WIDTH}
                >
                  {item.Campaign.Type === CAMPAIGN_CHANNEL_EMAIL && (
                    <span
                      data-tip={intl.formatMessage({
                        id: 'Load.Common.EmailChannel'
                      })}
                    >
                      <StyledIcon family='sli' icon='email-send' />
                    </span>
                  )}
                  {item.Campaign.Type === CAMPAIGN_CHANNEL_MESSENGER && (
                    <span
                      data-tip={intl.formatMessage({
                        id: 'Load.Common.MessengerChannel'
                      })}
                    >
                      <StyledIcon family='sli' icon='facebook-messanger' />
                    </span>
                  )}
                  {item.Campaign.Type === CAMPAIGN_CHANNEL_TELEGRAM && (
                    <>
                      <img
                        src={iconTelegram}
                        data-tip={intl.formatMessage({
                          id: 'Load.Common.TelegramChannel'
                        })}
                      />
                    </>
                  )}
                  {item.Campaign.ChannelDeleted && (
                    <span
                      data-tip={intl.formatMessage({
                        id: 'Load.ContentsCampaignList.ChannelDeleted'
                      })}
                    >
                      <StyledIconWarningTriangle
                        variant={ICON_FA_VARIANT_SOLID}
                        color='#CBCBCB'
                      />
                    </span>
                  )}
                </StyledDataListExtraColumn>

                <Grid cols={12}>
                  <GridCell span={heads[0].span}>
                    {!item.Campaign.ChannelDeleted ? (
                      <Link
                        variant={LINK_VARIANT_HIGHLIGHT}
                        onClick={() => handleEdit(item.Campaign)}
                      >
                        <TruncateAndTooltip text={item.Campaign.Name} />
                      </Link>
                    ) : (
                      <Text variant={TEXT_VARIANT_LIGHT}>
                        <TruncateAndTooltip text={item.Campaign.Name} />
                      </Text>
                    )}
                  </GridCell>
                  <GridCell span={heads[1].span}>
                    <ContentSource IdSource={item.Campaign.IdSource} />
                  </GridCell>
                  <GridCell span={heads[2].span}>
                    <Text variant={TEXT_VARIANT_LIGHT}>
                      {fromUTCtoZoneFormatted(
                        item.Campaign.UpdateDate,
                        TimeZoneCode,
                        'YYYY-MM-DD HH:mm'
                      )}
                    </Text>
                  </GridCell>
                  <GridCell span={heads[3].span}>
                    <Text variant={TEXT_VARIANT_LIGHT}>
                      {!item.LastSendDate && ' - '}
                      {item.LastSendDate &&
                        fromUTCtoZoneFormatted(
                          item.LastSendDate,
                          TimeZoneCode,
                          'YYYY-MM-DD HH:mm'
                        )}
                    </Text>
                  </GridCell>
                  <GridCell span={heads[4].span}>
                    {item.Campaign.Status === 0 ? (
                      <Text variant={TEXT_VARIANT_LIGHT}>
                        {intl.formatMessage({
                          id: 'Load.MailupCommon.Draft'
                        })}
                      </Text>
                    ) : (
                      <>
                        {!updateStatusInProgress && (
                          <>
                            <Toggle
                              disabled={item.Campaign.ChannelDeleted}
                              checked={item.Campaign.Enabled}
                              onChange={() => handelChangeStatus(item.Campaign)}
                              tooltip={
                                item.Campaign.ChannelDeleted
                                  ? intl.formatMessage({
                                      id: 'Load.ContentsCampaignList.NoPermissionMapiChannel'
                                    })
                                  : undefined
                              }
                            />
                          </>
                        )}

                        {updateStatusInProgress &&
                          updateStatusIdCampaign !== item.Campaign.Id && (
                            <Toggle
                              disabled={
                                updateStatusInProgress ||
                                item.Campaign.ChannelDeleted
                              }
                              checked={item.Campaign.Enabled}
                              onChange={() => handelChangeStatus(item.Campaign)}
                              tooltip={intl.formatMessage({
                                id: 'Load.ContentsCampaignList.NoPermissionMapiChannel'
                              })}
                            />
                          )}

                        {updateStatusInProgress &&
                          updateStatusIdCampaign === item.Campaign.Id && (
                            <SkeletonLoader
                              variant={SKELETON_LOADER_VARIANT_ONWHITE}
                            />
                          )}
                      </>
                    )}
                  </GridCell>
                </Grid>
                <DataListExtraColumn after width={EXTRA_COLUMN_AFTER_WIDTH}>
                  <TableActionMenu item={item.Campaign} />
                </DataListExtraColumn>
              </DataListRow>
            ))}
          </>
        )}
        {loadingTable && (
          <>
            {[...Array(pageSize)].map((item, key) => (
              <DataListRow key={key}>
                <Grid cols={12}>
                  <GridCell span={heads[0].span}>
                    <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                  </GridCell>
                  <GridCell span={heads[1].span}>
                    <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                  </GridCell>
                  <GridCell span={heads[2].span}>
                    <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                  </GridCell>
                  <GridCell span={heads[3].span}>
                    <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                  </GridCell>
                  <GridCell span={heads[4].span}>
                    <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                  </GridCell>
                </Grid>
                <DataListExtraColumn after width={EXTRA_COLUMN_AFTER_WIDTH} />
              </DataListRow>
            ))}
          </>
        )}
      </div>
      <Pagination />
    </Panel>
  )
}

export default Table
